<template>
  <div class="trainee-list-modal-overlay">
    <transition name="modal-expand">
      <div v-if="isVisible" class="trainee-list-modal" @click.stop>
        <font-awesome-icon class="window-close-icon" :icon="['fas', 'window-close']" @click="close" />
        <div class="modal-header">
          <div class="modal-title">
            <h2>Task Revisions</h2>
            <p class="modal-subtitle">
              <span class="type-badge" :style="typeStyle('Task')">Task</span>
              #{{ taskId }}<span v-if="taskLabel"> · {{ taskLabel }}</span>
            </p>
            <p v-if="!loading && items.length" class="modal-summary">
              {{ items.length }} {{ items.length === 1 ? 'change' : 'changes' }}
              · last change {{ relativeTime(items[0].time) }} by {{ items[0].user_name || 'System' }}
            </p>
          </div>
        </div>

        <div class="modal-body">
          <div v-if="error" class="state-box error">{{ error }}</div>
          <div v-else-if="loading" class="state-box">Loading revisions…</div>
          <div v-else-if="!items.length" class="state-box">No revisions for this task</div>
          <div v-else class="table-wrap">
            <RevisionTable :items="items" :now="now" group-by-day />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import RevisionTable from '../RevisionTable.vue';
import { normaliseRevisions, relativeTime, typeStyle } from '../revisionUtils';

export default {
  components: {
    RevisionTable,
  },
  emits: ['close'],

  data() {
    return {
      taskId: parseInt(localStorage.getItem('task_id')),
      taskLabel: '',
      items: [],
      loading: true,
      error: '',
      now: Date.now(),
      isVisible: false
    };
  },

  async mounted() {
    this.isVisible = true; // Ensures animation triggers when modal opens
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = { revisionable_type: 'Task', revisionable_id: this.taskId };

      const [revisionResult, taskResult] = await Promise.all([
        axios.get('/api/revisions/user', { headers, params }),
        // Only used for the heading, so a failure here (e.g. a deleted task) shouldn't block the modal
        axios.get(`/api/tasks/${this.taskId}`, { headers }).catch(() => null),
      ]);

      this.items = normaliseRevisions(revisionResult.data || []);
      const task = taskResult?.data;
      if (task) this.taskLabel = [task.task_no, task.subject].filter(Boolean).join(' · ');
      this.now = Date.now();
    } catch (error) {
      console.error('An error occurred while fetching data:', error);
      this.error = 'Revisions could not be loaded. Please try again.';
    } finally {
      this.loading = false;
    }
  },

  methods: {
    close() {
      this.$emit('close');
    },
    typeStyle,
    relativeTime(time) {
      return relativeTime(time, this.now);
    },
  },
}
</script>

<style scoped>
.trainee-list-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* Modal Expand Animation */
.modal-expand-enter-active,
.modal-expand-leave-active {
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;
}

.modal-expand-enter-from,
.modal-expand-leave-to {
  transform: scale(0.8);
  opacity: 0;
}

.modal-expand-enter-to,
.modal-expand-leave-from {
  transform: scale(1);
  opacity: 1;
}

/* Modal Box */
.trainee-list-modal {
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 1500px;
  max-height: 95vh;
  padding: 24px 28px 20px;
  /* Anchors the globally absolute-positioned .window-close-icon to the modal corner */
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: left;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.modal-title {
  min-width: 0;
  overflow-wrap: anywhere;
  /* Keeps long task names clear of the close icon */
  padding-right: 24px;
}

.modal-title h2 {
  margin: 0;
  font-size: 26px;
}

.modal-subtitle {
  margin: 6px 0 0;
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.modal-summary {
  margin: 4px 0 0;
  font-size: 14px;
  color: #666;
}

/* Only the revisions scroll, so the heading and Close button stay in view */
.modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.table-wrap {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow-x: auto;
}

.state-box {
  padding: 30px;
  text-align: center;
  border: 1px dashed #ccc;
  border-radius: 8px;
  color: #666;
}

.state-box.error {
  color: var(--error-text);
  border-style: solid;
  border-color: var(--error-border);
  background: var(--error-bg);
}

.type-badge {
  --type-hue: 0;
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 4px;
  margin-right: 4px;
  color: hsl(var(--type-hue), 55%, 28%);
  background: hsl(var(--type-hue), 70%, 93%);
  border: 1px solid hsl(var(--type-hue), 45%, 80%);
}

.button-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.ins-close-button {
  background-color: #6c757d;
  border: 1px solid #4d5358;
  color: white;
  width: 120px;
  padding: 5px 20px;
  border-radius: 5px;
}

.ins-close-button:hover {
  background-color: #4d5358;
}
</style>
